import Modal from "@mui/material/Modal";
import { useTheme } from "@mui/system";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { Cancel } from "../../assets/icons";
import { closeDialogPopup } from "../../redux/reducers/dialogPopup";
import { colors } from "../../utils";
import { device } from "../../utils/device";
import Button from "../Button";
import TextView from "../TextView";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  text-align: left;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  position: relative;
  max-width: ${({ $width }) => $width || "600px"};
  width: 90%;
  ${({ $width }) =>
    $width &&
    `
    max-height: 80vh;
    overflow-y: auto;
  `};

  @media ${device.mobilePortrait} {
    width: 95%;
    padding: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${colors.lightBorder};
`;

const IconWrapper = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: center;

  img {
    width: 24px;
    height: 24px;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
`;

const CloseIcon = styled.div`
  position: absolute;
  right: 24px;
  top: 24px;
  cursor: pointer;
  width: 24px;
`;

const Content = styled.div`
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  justify-content: flex-end;
  grid-gap: 10px;

  @media ${device.mobilePortrait} {
    flex-direction: column;
  }
`;

const DialogPopup = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    isOpen,
    title,
    text,
    icon,
    primaryButtonTitle,
    primaryButtonOnClick,
    secondaryButtonTitle = "Cancel",
    secondaryButtonOnClick = () => dispatch(closeDialogPopup()),
    disableClose,
    extraContent,
    extraButtonTitle,
    extraButtonOnClick,
    autoWidth,
  } = useSelector((state) => state.dialogPopup);

  const handleClose = () => {
    dispatch(closeDialogPopup());
  };

  const handleCancel = () => {
    if (secondaryButtonOnClick) secondaryButtonOnClick();
    !disableClose && handleClose();
  };

  const handleConfirm = () => {
    if (primaryButtonOnClick) primaryButtonOnClick();
    !disableClose && handleClose();
  };

  return (
    <StyledModal
      open={isOpen}
      onClose={disableClose ? undefined : handleClose}
      aria-labelledby="dialog-popup"
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <ModalContainer $width={autoWidth ? "75%" : undefined}>
        {!disableClose && (
          <CloseIcon>
            <Cancel onClick={handleClose} />
          </CloseIcon>
        )}

        <Header>
          {icon && (
            <IconWrapper>
              <img src={icon} alt="Dialog Icon" />
            </IconWrapper>
          )}
          <Title>{title}</Title>
        </Header>

        <Content>
          {!!text && <TextView>{text}</TextView>}
          {!!extraContent && <div>{extraContent}</div>}
        </Content>

        <ButtonContainer>
          {secondaryButtonTitle && (
            <Button variant="secondary" onClick={handleCancel}>
              {secondaryButtonTitle}
            </Button>
          )}
          {extraButtonTitle && (
            <Button onClick={extraButtonOnClick} variant="outlined">
              {extraButtonTitle}
            </Button>
          )}
          {primaryButtonTitle && (
            <Button onClick={handleConfirm}>{primaryButtonTitle}</Button>
          )}
        </ButtonContainer>
      </ModalContainer>
    </StyledModal>
  );
};

export default DialogPopup;
